import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Session } from "@supabase/supabase-js";
import {
  Activity,
  AlertTriangle,
  Coins,
  Database,
  Film,
  LogOut,
  ShieldAlert,
  RefreshCw,
  Search,
  Settings,
  ShoppingBag,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ModerationQueueCase } from "@vybra/shared";
import {
  adminApi,
  adminSessionUserId,
  confirmOptions,
  shouldClearAdminCache,
  supabase,
} from "./lib";

type Page =
  | "overview"
  | "moderation"
  | "jobs"
  | "users"
  | "purchases"
  | "catalog"
  | "failures"
  | "settings";
type Dashboard = {
  jobs: Array<{
    status: string;
    count: number;
    provider_cost: number;
    avg_latency_ms: number;
  }>;
  purchases: { events: number; coins: number };
  wallet: Array<{ entry_type: string; coins: number }>;
};
type Job = {
  id: string;
  user_id: string;
  task_uuid: string;
  model_key: string;
  internal_status: string;
  progress: number | null;
  expected_coins: number;
  expected_provider_cost_usd: number;
  actual_provider_cost_usd: number | null;
  created_at: string;
  updated_at: string;
};
type UserRow = {
  id: string;
  display_name: string;
  handle: string;
  status: string;
  created_at: string;
  generations: number;
  projects: number;
};
type Purchase = {
  id: string;
  user_id: string | null;
  environment: string;
  product_id: string;
  transaction_id: string;
  event_id: string;
  event_type: string;
  coin_amount: number;
  verification_status: string;
  processed_at: string | null;
  created_at: string;
};
type PurchaseAdjustment = {
  id: string;
  product_id: string;
  transaction_id: string;
  adjustment_type: string;
  requested_coin_amount: number;
  applied_coin_amount: number;
  status: string;
  reason: string | null;
  created_at: string;
};
type Failure = {
  id: string;
  queue_name: string;
  message_id: string;
  attempts: number;
  error_message: string;
  received_at: string;
  replayed_at: string | null;
};

const nav: Array<[Page, string, typeof Activity]> = [
  ["overview", "Overview", Activity],
  ["moderation", "Moderation", ShieldAlert],
  ["jobs", "Generations", Film],
  ["users", "Users & wallets", Users],
  ["purchases", "Purchases", ShoppingBag],
  ["catalog", "Catalog", Database],
  ["failures", "Failures", AlertTriangle],
  ["settings", "Controls", Settings],
];

export function App() {
  const cache = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);
  const [page, setPage] = useState<Page>("overview");
  const currentUserId = useRef<string | null>(null);
  useEffect(() => {
    const applySession = (next: Session | null) => {
      const nextUserId = adminSessionUserId(next);
      if (shouldClearAdminCache(currentUserId.current, next)) cache.clear();
      currentUserId.current = nextUserId;
      setSession(next);
    };
    void supabase.auth.getSession().then(({ data }) => {
      applySession(data.session);
      setReady(true);
    });
    const { data } = supabase.auth.onAuthStateChange((_event, next) =>
      applySession(next),
    );
    return () => data.subscription.unsubscribe();
  }, [cache]);
  if (!ready) return <div className="center">Loading operations…</div>;
  if (!session && import.meta.env.VITE_ENABLE_TEST_AUTH !== "true")
    return <Login />;
  return (
    <div className="shell">
      <aside>
        <div className="brand">
          <div className="mark">V</div>
          <div>
            <b>Vybra Videos</b>
            <span>Operations</span>
          </div>
        </div>
        <nav>
          {nav.map(([id, label, Icon]) => (
            <button
              key={id}
              className={page === id ? "active" : ""}
              onClick={() => setPage(id)}
            >
              <Icon size={17} />
              {label}
            </button>
          ))}
        </nav>
        <button
          className="signout"
          onClick={() => void supabase.auth.signOut()}
        >
          <LogOut size={15} /> Sign out
        </button>
      </aside>
      <main>
        <Header page={page} />
        {page === "overview" ? <Overview session={session} /> : null}
        {page === "moderation" ? <Moderation session={session} /> : null}
        {page === "jobs" ? <Jobs session={session} /> : null}
        {page === "users" ? <UsersPage session={session} /> : null}
        {page === "purchases" ? <Purchases session={session} /> : null}
        {page === "catalog" ? <Catalog session={session} /> : null}
        {page === "failures" ? <Failures session={session} /> : null}
        {page === "settings" ? <Controls session={session} /> : null}
      </main>
    </div>
  );
}

function Login() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  async function submit() {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    });
    if (error) alert(error.message);
    else setSent(true);
  }
  return (
    <div className="login">
      <div className="mark large">V</div>
      <h1>Vybra Videos operations</h1>
      <p>Restricted to role-approved Vybra administrators.</p>
      <input
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        placeholder="Admin email"
        type="email"
      />
      <button className="primary" onClick={() => void submit()}>
        {sent ? "Check your inbox" : "Send secure sign-in link"}
      </button>
    </div>
  );
}
function Header({ page }: { page: Page }) {
  const title = nav.find(([id]) => id === page)?.[1] ?? "Operations";
  return (
    <header>
      <div>
        <p>VYBRA VIDEOS</p>
        <h1>{title}</h1>
      </div>
      <div className="live">
        <span /> Live data
      </div>
    </header>
  );
}

function Overview({ session }: { session: Session | null }) {
  const query = useQuery({
    queryKey: ["admin", "dashboard"],
    queryFn: () => adminApi<Dashboard>("/v1/admin/dashboard", session),
    refetchInterval: 30_000,
  });
  const totals = useMemo(
    () => ({
      success:
        query.data?.jobs.find((item) => item.status === "succeeded")?.count ??
        0,
      failed:
        query.data?.jobs.find((item) => item.status === "failed")?.count ?? 0,
      pending:
        query.data?.jobs
          .filter(
            (item) =>
              !["succeeded", "failed", "cancelled"].includes(item.status),
          )
          .reduce((sum, item) => sum + item.count, 0) ?? 0,
      cost:
        query.data?.jobs.reduce(
          (sum, item) => sum + Number(item.provider_cost),
          0,
        ) ?? 0,
      spent:
        query.data?.wallet.find((item) => item.entry_type === "settle")
          ?.coins ?? 0,
    }),
    [query.data],
  );
  return (
    <>
      <div className="metric-strip">
        <Metric label="Successful generations" value={totals.success} />
        <Metric label="Pending / unresolved" value={totals.pending} />
        <Metric label="Failed" value={totals.failed} />
        <Metric label="Provider cost" value={`$${totals.cost.toFixed(2)}`} />
        <Metric label="Coins settled" value={totals.spent} />
      </div>
      <section>
        <SectionTitle
          title="Generation state"
          copy="Current counts and recorded provider cost by terminal or active state."
        />
        <Table
          headers={["Status", "Jobs", "Provider cost", "Average latency"]}
          rows={(query.data?.jobs ?? []).map((item) => [
            <Status key={item.status} value={item.status} />,
            item.count,
            `$${Number(item.provider_cost).toFixed(3)}`,
            formatDuration(item.avg_latency_ms),
          ])}
        />
      </section>
    </>
  );
}
function Jobs({ session }: { session: Session | null }) {
  const cache = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ["admin", "jobs"],
    queryFn: () => adminApi<Job[]>("/v1/admin/jobs", session),
  });
  const detail = useQuery({
    queryKey: ["admin", "job", selected],
    queryFn: () =>
      adminApi<Record<string, unknown>>(`/v1/admin/jobs/${selected}`, session),
    enabled: Boolean(selected),
  });
  const reconcile = useMutation({
    mutationFn: (id: string) =>
      adminApi(`/v1/admin/jobs/${id}/reconcile`, session, confirmOptions()),
    onSuccess: () => cache.invalidateQueries({ queryKey: ["admin", "jobs"] }),
  });
  return (
    <div className="split">
      <section>
        <SectionTitle
          title="Latest generations"
          copy="Provider state, charged coins, cost, and reconciliation status."
        />
        <Table
          headers={["Created", "Model", "Status", "Coins", "Provider cost"]}
          rows={(query.data ?? []).map((job) => [
            new Date(job.created_at.replace(" ", "T") + "Z").toLocaleString(),
            job.model_key,
            <Status key={job.id} value={job.internal_status} />,
            job.expected_coins,
            job.actual_provider_cost_usd === null
              ? "—"
              : `$${job.actual_provider_cost_usd.toFixed(3)}`,
          ])}
          onRow={(index) => setSelected(query.data?.[index]?.id ?? null)}
        />
      </section>
      {selected ? (
        <aside className="inspector">
          <button className="close" onClick={() => setSelected(null)}>
            ×
          </button>
          <p className="eyebrow">JOB DETAIL</p>
          <h2>{selected}</h2>
          <pre>{JSON.stringify(detail.data, null, 2)}</pre>
          <button
            className="danger"
            onClick={() =>
              window.confirm("Queue reconciliation for this job?") &&
              reconcile.mutate(selected)
            }
          >
            <RefreshCw size={14} /> Reconcile
          </button>
        </aside>
      ) : null}
    </div>
  );
}
function UsersPage({ session }: { session: Session | null }) {
  const [selected, setSelected] = useState<string | null>(null);
  const [amount, setAmount] = useState("100");
  const [reason, setReason] = useState("");
  const [adjustmentId, setAdjustmentId] = useState(() => crypto.randomUUID());
  const query = useQuery({
    queryKey: ["admin", "users"],
    queryFn: () => adminApi<UserRow[]>("/v1/admin/users", session),
  });
  const wallet = useQuery({
    queryKey: ["admin", "wallet", selected],
    queryFn: () =>
      adminApi<{
        balance: Record<string, number>;
        ledger: { entries: unknown[] };
      }>(`/v1/admin/users/${selected}/wallet`, session),
    enabled: Boolean(selected),
  });
  const adjust = useMutation({
    mutationFn: (direction: "credit" | "debit") =>
      adminApi(`/v1/admin/users/${selected}/wallet/adjust`, session, {
        ...confirmOptions({
          adjustmentId,
          direction,
          amount: Number(amount),
          reason,
        }),
      }),
    onSuccess: () => {
      setAdjustmentId(crypto.randomUUID());
      return wallet.refetch();
    },
  });
  return (
    <div className="split">
      <section>
        <SectionTitle
          title="Vybra Videos users"
          copy="App membership and activity. Global Supabase identities are not modified here."
        />
        <Table
          headers={["Creator", "Handle", "Status", "Generations", "Projects"]}
          rows={(query.data ?? []).map((user) => [
            user.display_name,
            `@${user.handle}`,
            <Status key={user.id} value={user.status} />,
            user.generations,
            user.projects,
          ])}
          onRow={(index) => {
            adjust.reset();
            setSelected(query.data?.[index]?.id ?? null);
            setAdjustmentId(crypto.randomUUID());
          }}
        />
      </section>
      {selected ? (
        <aside className="inspector">
          <button
            className="close"
            onClick={() => {
              adjust.reset();
              setSelected(null);
              setAdjustmentId(crypto.randomUUID());
            }}
          >
            ×
          </button>
          <p className="eyebrow">AUTHORITATIVE WALLET</p>
          <h2>{wallet.data?.balance.available ?? "—"} available</h2>
          <dl>
            <dt>Reserved</dt>
            <dd>{wallet.data?.balance.reserved ?? "—"}</dd>
            <dt>Lifetime spent</dt>
            <dd>{wallet.data?.balance.lifetimeSpent ?? "—"}</dd>
          </dl>
          <label>
            Adjustment amount
            <input
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              type="number"
              min="1"
              disabled={adjust.isError || adjust.isPending}
            />
          </label>
          <label>
            Required reason
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              disabled={adjust.isError || adjust.isPending}
            />
          </label>
          {adjust.isError ? (
            <p className="error">
              The request did not finish cleanly. Retry the same adjustment so
              its operation ID remains idempotent, or close this user to abandon
              it.
            </p>
          ) : null}
          <div className="actions">
            <button
              disabled={
                reason.length < 5 ||
                adjust.isPending ||
                (adjust.isError && adjust.variables !== "credit")
              }
              onClick={() =>
                window.confirm("Credit this wallet? This action is audited.") &&
                adjust.mutate("credit")
              }
            >
              Credit
            </button>
            <button
              className="danger"
              disabled={
                reason.length < 5 ||
                adjust.isPending ||
                (adjust.isError && adjust.variables !== "debit")
              }
              onClick={() =>
                window.confirm("Debit this wallet? This action is audited.") &&
                adjust.mutate("debit")
              }
            >
              Debit
            </button>
          </div>
        </aside>
      ) : null}
    </div>
  );
}
function Purchases({ session }: { session: Session | null }) {
  const cache = useQueryClient();
  const query = useQuery({
    queryKey: ["admin", "purchases"],
    queryFn: () => adminApi<Purchase[]>("/v1/admin/purchases", session),
  });
  const adjustments = useQuery({
    queryKey: ["admin", "purchase-adjustments"],
    queryFn: () =>
      adminApi<PurchaseAdjustment[]>("/v1/admin/purchase-adjustments", session),
  });
  const reconcile = useMutation({
    mutationFn: (id: string) =>
      adminApi(
        `/v1/admin/purchases/${id}/reconcile`,
        session,
        confirmOptions(),
      ),
    onSuccess: () =>
      cache.invalidateQueries({ queryKey: ["admin", "purchases"] }),
  });
  return (
    <>
      <section>
        <SectionTitle
          title="Verified purchase events"
          copy="Transaction IDs are idempotency keys. Reconciliation never grants the same purchase twice."
        />
        <Table
          headers={[
            "Created",
            "Product",
            "Coins",
            "Environment",
            "State",
            "Action",
          ]}
          rows={(query.data ?? []).map((item) => [
            new Date(item.created_at.replace(" ", "T") + "Z").toLocaleString(),
            item.product_id,
            item.coin_amount,
            item.environment,
            <Status key={item.id} value={item.verification_status} />,
            <button
              key={`${item.id}-r`}
              className="tiny"
              onClick={() =>
                window.confirm("Reconcile this verified event?") &&
                reconcile.mutate(item.id)
              }
            >
              Reconcile
            </button>,
          ])}
        />
      </section>
      <section>
        <SectionTitle
          title="Refunds and reversals"
          copy="Automatic debits never make a wallet negative; partial clawbacks remain marked for review."
        />
        <Table
          headers={[
            "Created",
            "Product",
            "Type",
            "Requested",
            "Applied",
            "State",
          ]}
          rows={(adjustments.data ?? []).map((item) => [
            item.created_at,
            item.product_id,
            item.adjustment_type,
            item.requested_coin_amount,
            item.applied_coin_amount,
            <Status key={item.id} value={item.status} />,
          ])}
        />
      </section>
    </>
  );
}
function Catalog({ session }: { session: Session | null }) {
  const cache = useQueryClient();
  const pricing = useQuery({
    queryKey: ["admin", "pricing"],
    queryFn: () =>
      adminApi<{
        version: string;
        models: Array<Record<string, unknown>>;
        packages: Array<Record<string, unknown>>;
      }>("/v1/admin/pricing", session),
  });
  const templates = useQuery({
    queryKey: ["admin", "templates"],
    queryFn: () =>
      adminApi<
        Array<{
          id: string;
          title: string;
          category: string;
          enabled: boolean;
          featured: boolean;
          trending: boolean;
        }>
      >("/v1/admin/templates", session),
  });
  const patch = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      adminApi(`/v1/admin/templates/${id}`, session, {
        method: "PATCH",
        headers: { "x-admin-confirmation": "confirm" },
        body: JSON.stringify({ enabled }),
      }),
    onSuccess: () =>
      cache.invalidateQueries({ queryKey: ["admin", "templates"] }),
  });
  return (
    <>
      <section>
        <SectionTitle
          title={`Pricing ${pricing.data?.version ?? ""}`}
          copy="Versioned launch rates and store products. Rate changes require reviewed code and deployment."
        />
        <pre className="code">{JSON.stringify(pricing.data, null, 2)}</pre>
      </section>
      <section>
        <SectionTitle
          title="Template catalog"
          copy="All templates, including unpublished entries. Visibility changes are confirmed and audited."
        />
        <Table
          headers={[
            "Template",
            "Category",
            "Featured",
            "Trending",
            "Visibility",
          ]}
          rows={(templates.data ?? []).map((item) => [
            item.title,
            item.category,
            item.featured ? "Yes" : "No",
            item.trending ? "Yes" : "No",
            <button
              key={`${item.id}-visibility`}
              className={item.enabled ? "tiny" : "tiny danger"}
              onClick={() =>
                window.confirm(
                  `${item.enabled ? "Disable" : "Enable"} ${item.title}?`,
                ) && patch.mutate({ id: item.id, enabled: !item.enabled })
              }
            >
              {item.enabled ? "Published" : "Hidden"}
            </button>,
          ])}
        />
      </section>
    </>
  );
}
function Failures({ session }: { session: Session | null }) {
  const cache = useQueryClient();
  const query = useQuery({
    queryKey: ["admin", "failures"],
    queryFn: () => adminApi<Failure[]>("/v1/admin/failures", session),
  });
  const replay = useMutation({
    mutationFn: (id: string) =>
      adminApi(`/v1/admin/failures/${id}/replay`, session, confirmOptions()),
    onSuccess: () =>
      cache.invalidateQueries({ queryKey: ["admin", "failures"] }),
  });
  return (
    <section>
      <SectionTitle
        title="Dead-letter queue"
        copy="Messages that exhausted automatic retries. Inspect before replaying."
      />
      <Table
        headers={["Received", "Queue", "Attempts", "Error", "Action"]}
        rows={(query.data ?? []).map((item) => [
          item.received_at,
          item.queue_name,
          item.attempts,
          item.error_message,
          item.replayed_at ? (
            "Replayed"
          ) : (
            <button
              key={item.id}
              className="tiny danger"
              onClick={() =>
                window.confirm("Replay this failed message?") &&
                replay.mutate(item.id)
              }
            >
              Replay
            </button>
          ),
        ])}
      />
    </section>
  );
}
function Controls({ session }: { session: Session | null }) {
  const cache = useQueryClient();
  const query = useQuery({
    queryKey: ["admin", "config"],
    queryFn: () =>
      adminApi<Record<string, { value: unknown; updatedAt: string }>>(
        "/v1/admin/config",
        session,
      ),
  });
  const patch = useMutation({
    mutationFn: ({ key, value }: { key: string; value: boolean }) =>
      adminApi(`/v1/admin/config/${key}`, session, {
        method: "PATCH",
        headers: { "x-admin-confirmation": "confirm" },
        body: JSON.stringify({ value }),
      }),
    onSuccess: () => cache.invalidateQueries({ queryKey: ["admin", "config"] }),
  });
  const keys = [
    "generation_enabled",
    "model_mini_enabled",
    "model_fast_enabled",
    "model_quality_enabled",
    "model_cinema_enabled",
    "cameos_enabled",
    "feed_enabled",
  ];
  return (
    <section>
      <SectionTitle
        title="Generation controls"
        copy="Emergency switches take effect before a quote is issued and are audited."
      />
      <div className="control-list">
        {keys.map((key) => (
          <div key={key}>
            <div>
              <b>{key.replaceAll("_", " ")}</b>
              <span>
                Last changed {query.data?.[key]?.updatedAt ?? "from seed"}
              </span>
            </div>
            <button
              className={
                query.data?.[key]?.value !== false ? "toggle on" : "toggle"
              }
              onClick={() =>
                window.confirm(`Change ${key}?`) &&
                patch.mutate({ key, value: query.data?.[key]?.value === false })
              }
            >
              {query.data?.[key]?.value !== false ? "Enabled" : "Disabled"}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

const SLA_LABEL: Record<string, string> = {
  normal: "Normal",
  due_soon: "Due soon",
  urgent: "Urgent",
  breached: "SLA breached",
};

const KIND_LABEL: Record<string, string> = {
  ncii: "Non-consensual imagery",
  likeness_objection: "Likeness objection",
  report: "Reported",
  auto_hidden: "Auto-hidden",
  manual_review: "Manual review",
  comment: "Reported comment",
};

const DECISIONS: { value: string; label: string }[] = [
  { value: "approve", label: "Approve" },
  { value: "keep_hidden", label: "Keep hidden" },
  { value: "remove", label: "Remove" },
  { value: "remove_warn", label: "Remove + warn" },
  { value: "remove_restrict", label: "Remove + restrict publishing" },
  { value: "remove_suspend", label: "Remove + suspend community access" },
];

function Moderation({ session }: { session: Session | null }) {
  const cache = useQueryClient();
  const [selected, setSelected] = useState<ModerationQueueCase | null>(null);
  const [reason, setReason] = useState("");

  const queue = useQuery({
    queryKey: ["admin", "moderation-queue"],
    queryFn: () =>
      adminApi<{ cases: ModerationQueueCase[] }>(
        "/v1/admin/moderation/queue",
        session,
      ),
    // Time-sensitive work, so the SLA column must not go stale on screen.
    refetchInterval: 60_000,
  });

  const decide = useMutation({
    mutationFn: (decision: string) =>
      adminApi(
        selected?.kind === "comment"
          ? `/v1/admin/moderation/comments/${selected.commentId}`
          : "/v1/admin/moderation/decide",
        session,
        {
          ...confirmOptions({
            projectId: selected?.projectId,
            decision,
            reason: reason.trim() || null,
          }),
        },
      ),
    onSuccess: () => {
      setSelected(null);
      setReason("");
      return cache.invalidateQueries({
        queryKey: ["admin", "moderation-queue"],
      });
    },
  });

  const cases = queue.data?.cases ?? [];
  const breached = cases.filter((entry) => entry.sla === "breached").length;

  return (
    <div className="split">
      <section>
        <SectionTitle
          title="Moderation queue"
          copy={
            breached > 0
              ? `${breached} case(s) past the 24-hour review target. Highest risk is listed first.`
              : "Highest risk first, then longest waiting. Target is 24 hours."
          }
        />
        <Table
          headers={["Case", "Publisher", "Reasons", "Reporters", "Waiting"]}
          rows={cases.map((entry) => [
            <span key={`${entry.id}-kind`}>
              {entry.highRisk ? "\u26A0 " : ""}
              {KIND_LABEL[entry.kind] ?? entry.kind}
            </span>,
            entry.publisher ? `@${entry.publisher.handle}` : "\u2014",
            entry.reasons.length ? entry.reasons.join(", ") : "\u2014",
            entry.distinctReporters,
            <span key={`${entry.id}-sla`} className={`sla sla-${entry.sla}`}>
              {Math.floor(entry.ageHours)}h · {SLA_LABEL[entry.sla]}
            </span>,
          ])}
          onRow={(index) => {
            decide.reset();
            setReason("");
            setSelected(cases[index] ?? null);
          }}
        />
      </section>
      {selected ? (
        <aside className="inspector">
          <button className="close" onClick={() => setSelected(null)}>
            Close
          </button>
          <h3>{selected.title ?? "Untitled"}</h3>
          <dl className="detail">
            <dt>Case</dt>
            <dd>{KIND_LABEL[selected.kind] ?? selected.kind}</dd>
            <dt>Publisher</dt>
            <dd>
              {selected.publisher
                ? `${selected.publisher.displayName} (@${selected.publisher.handle})`
                : "\u2014"}
            </dd>
            <dt>Prior violations</dt>
            <dd>{selected.priorViolations}</dd>
            <dt>Distinct reporters</dt>
            <dd>{selected.distinctReporters}</dd>
            <dt>Reasons</dt>
            <dd>{selected.reasons.join(", ") || "\u2014"}</dd>
            <dt>Moderation state</dt>
            <dd>{selected.moderationState ?? "\u2014"}</dd>
            <dt>Eligibility</dt>
            <dd>{selected.eligibility ?? "\u2014"}</dd>
            <dt>Waiting</dt>
            <dd>
              {Math.floor(selected.ageHours)}h · {SLA_LABEL[selected.sla]}
            </dd>
          </dl>
          <input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Reason (recorded in the audit log)"
          />
          {decide.isError ? (
            <p className="error">
              That decision did not apply. Retry it before moving on.
            </p>
          ) : null}
          <div className="decisions">
            {DECISIONS.filter(
              (option) =>
                selected.kind !== "comment" ||
                ["approve", "remove"].includes(option.value),
            ).map((option) => (
              <button
                key={option.value}
                disabled={decide.isPending || !selected.projectId}
                onClick={() => decide.mutate(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <p className="hint">
            {selected.kind === "comment"
              ? "This decision affects the comment only, not the video or its creator."
              : "Removing keeps the private project. It only prevents the video from returning to the public feed."}
          </p>
        </aside>
      ) : null}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
function SectionTitle({ title, copy }: { title: string; copy: string }) {
  return (
    <div className="section-title">
      <div>
        <h2>{title}</h2>
        <p>{copy}</p>
      </div>
      <Search size={18} />
    </div>
  );
}
function Status({ value }: { value: string }) {
  return (
    <span className={`status ${value}`}>{value.replaceAll("_", " ")}</span>
  );
}
function Table({
  headers,
  rows,
  onRow,
}: {
  headers: string[];
  rows: React.ReactNode[][];
  onRow?: (index: number) => void;
}) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={index}
              onClick={() => onRow?.(index)}
              className={onRow ? "clickable" : ""}
            >
              {row.map((cell, cellIndex) => (
                <td key={cellIndex}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 ? (
        <div className="empty">No records in this view.</div>
      ) : null}
    </div>
  );
}
function formatDuration(ms: number) {
  if (!ms) return "—";
  return ms < 60_000
    ? `${Math.round(ms / 1000)}s`
    : `${Math.round(ms / 60_000)}m`;
}
